import { useState, useEffect } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import BoQComparisonView from './components/BoQComparisonView';
import StepperActions from './components/StepperActions';
import { fetchPriceComparison, markOfferSubmissionAssessmentError } from './offer-evaluation.slice';
import BoQMarkErrorDialog, { BoQMarkErrorDialogResult } from './components/BoQMarkErrorDialog';

const RECHNERISCHE_PRUEFUNG_R2_REASON_CATALOG = [
    ['PRICE_TOO_LOW', 'Price is too low'],
    ['PRICE_TOO_HIGH', 'Price is too high'],
] as const;

export default function OfferEvaluationStepPriceComparison() {
    const { t } = useTranslation();
    const { tender, priceComparison } = useAppSelector((state) => state.offerEvaluation);
    const dispatch = useAppDispatch();

    const [dialogOpenedForSubmissionItem, setDialogOpenedForSubmissionItem] = useState<[string, string] | null>(null);
    const dialogOpenForOfferSubmission = priceComparison?.offerSubmissions.find(
        ({ id }) => id === dialogOpenedForSubmissionItem?.[0],
    );
    const dialogOpenForOfferSubmissionItem = dialogOpenForOfferSubmission?.items.find(
        ({ ordinal }) => ordinal === dialogOpenedForSubmissionItem?.[1],
    );

    useEffect(() => {
        if (!tender) {
            return;
        }
        dispatch(fetchPriceComparison(tender.id));
    }, [dispatch, tender]);

    const handleMarkErrorDialogOpen = (offerSubmissionId: string, ordinal: string) => {
        setDialogOpenedForSubmissionItem([offerSubmissionId, ordinal]);
    };

    const handleMarkErrorDialogClose = async (result: BoQMarkErrorDialogResult) => {
        if (result.action === 'confirm' && dialogOpenedForSubmissionItem) {
            const [offerSubmissionId, ordinal] = dialogOpenedForSubmissionItem;

            await dispatch(
                markOfferSubmissionAssessmentError({
                    offerSubmissionId,
                    ordinal,
                    type: 'RECHNERISCHE_PRUEFUNG_R2',
                    reason: result.reason,
                }),
            );
        }

        setDialogOpenedForSubmissionItem(null);
    };

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Price Comparison')}
            </Typography>

            {!priceComparison ? (
                <Skeleton variant="rectangular" width="100%" height={800} />
            ) : (
                <BoQComparisonView priceComparison={priceComparison} onMarkError={handleMarkErrorDialogOpen} />
            )}

            <BoQMarkErrorDialog
                title="Rechnerische Prüfung (R2)"
                description={t(
                    'Describe below why the Rechnerische Prüfung (R2) is invalid. This is, for example, a significant deviation from other offers.',
                )}
                initialReason={
                    dialogOpenForOfferSubmissionItem?.assessmentFailReasons['RECHNERISCHE_PRUEFUNG_R2'] ?? ''
                }
                open={Boolean(dialogOpenedForSubmissionItem)}
                reasonCatalog={RECHNERISCHE_PRUEFUNG_R2_REASON_CATALOG}
                onClose={handleMarkErrorDialogClose}
            />

            <StepperActions />
        </>
    );
}
